import { IFacilityRole } from "api/rpc/2024-04/facilityAdmin/client/admin/admin";
import { IUserPermissions } from "redux/reducers/models/user";

type RoleRef = { id: number };

/**
 * Apply a role add/remove onto the staff member's current permission flags.
 * A permission they turned on that none of the previous roles enabled stays on.
 * A permission enabled only by a removed role turns off.
 * A role that is still assigned keeps the permissions it enables.
 */
export function permissionsAfterRoleChange(
  roleCatalog: IFacilityRole[] | null | undefined,
  previousRoles: RoleRef[] | undefined,
  nextRoles: RoleRef[],
  currentPermissions: IUserPermissions,
): IUserPermissions {
  const nextPermissions = { ...currentPermissions };
  const flags = nextPermissions as Record<string, unknown>;
  const previouslyGranted = grantedPermissionKeys(roleCatalog, previousRoles ?? []);
  const stillGranted = grantedPermissionKeys(roleCatalog, nextRoles);

  Object.keys(flags).forEach(key => {
    if (typeof flags[key] !== "boolean") {
      return;
    }

    if (flags[key] === true && !previouslyGranted.has(key)) {
      return;
    }

    flags[key] = stillGranted.has(key);
  });

  stillGranted.forEach(key => {
    if (typeof flags[key] === "boolean" || !(key in flags)) {
      flags[key] = true;
    }
  });

  return nextPermissions;
}

function grantedPermissionKeys(roleCatalog: IFacilityRole[] | null | undefined, roles: RoleRef[]) {
  const keys = new Set<string>();

  roles.forEach(role => {
    roleCatalog
      ?.find(item => item.id === role.id)
      ?.permissions?.forEach(permission => {
        if (permission?.pivot?.boolean_value && permission.key) {
          keys.add(permission.key);
        }
      });
  });

  return keys;
}
